import { type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import {
  PolicyKey,
  PolicyMode,
  type PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import {
  useOrganizationPolicies,
  useToggleOrganizationPolicy,
} from '@/hooks/api/useOrganizationPolicies';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { OrganizationRole } from '@/lib/roles';
import { useToast } from '@/hooks/use-toast';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { MODE_LABELS } from '@/components/policylist/constants';
import { ThreatModelingPage } from '@/components/AiInstructionCatalog/ThreatModelingPage';

/**
 * KIR-3925: organization Threat Modeling page. Reads/writes the
 * `threat-modeling` policy mode via the org policy hooks (owner/manager may
 * edit) and renders the shared catalog page.
 */
export default function ThreatModelingTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { hasAccess } = useRoleCheck({
    param: 'organizationId',
    anyOf: [OrganizationRole.Owner, OrganizationRole.Manager],
  });

  const { data: policies } = useOrganizationPolicies(organizationId);
  const togglePolicy = useToggleOrganizationPolicy(organizationId);
  const { toast } = useToast();

  if (!organizationId) {
    return null;
  }

  const policy = policies?.find(p => p.key === PolicyKey.ThreatModeling);
  const mode = (policy?.mode ?? PolicyMode.Off) as PolicyModeType;
  const policyName = policy?.name ?? 'Threat Modeling';

  // Route through createMutationHandler so a mode change shows the same
  // success/error toast as every other policy on the Policies page.
  const onModeChange = (next: PolicyModeType): void => {
    void createMutationHandler(togglePolicy, toast, {
      successMessage: `Policy ${policyName} set to ${MODE_LABELS[next]}`,
      errorMessage: `Failed to update ${policyName}`,
    })({
      policyKey: PolicyKey.ThreatModeling,
      enabled: next !== PolicyMode.Off,
      mode: next,
    });
  };

  return (
    <ThreatModelingPage
      organizationId={organizationId}
      canEdit={hasAccess}
      mode={mode}
      supportsEnforce={policy?.supportsEnforce ?? false}
      enforceDisabledReason={policy?.enforceDisabledReason}
      enforceTooltip={policy?.enforceTooltip}
      onModeChange={onModeChange}
      modePending={togglePolicy.isPending}
    />
  );
}
